import React, { useContext, useRef } from 'react';
import { Button, Icon, Typography } from '@/components/common';
import {
	UseFieldArrayAppend,
	UseFieldArrayRemove,
	useFormContext,
} from 'react-hook-form';
import clsx from 'clsx';
import { CaseContext } from '@/context/CaseContext';
import { CaseDetailsLawyer, Option } from '@/interfaces';
import { brandsService } from '@/api/brands';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import Icons from '@/const/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { GET_BRANDS } from '@/api';
import { InputListV3 } from '@/components/form/input-list-v3';
import { ListBrands } from '@/interfaces/brands';
import { useDebounce } from 'use-debounce';
import { InputList } from '@/components/form';

interface CompanyInformationProps {
	applyRule: boolean;
	listStates: Option[] | undefined;
	fields: Record<'id', string>[];
	listEntityTypes: Option[];
	caseDetails: CaseDetailsLawyer | undefined;
	append: UseFieldArrayAppend<any, 'companies'>;
	remove: UseFieldArrayRemove;
	isPublished?: boolean;
}

const CaseCompanyInformation: React.FC<CompanyInformationProps> = ({
	applyRule,
	listStates,
	fields,
	listEntityTypes,
	caseDetails,
	append,
	remove,
	isPublished,
}) => {
	const refCreation = useRef<boolean>(false);
	const { toastSuccess, toastError } = useNotification();
	const { isCaseReadOnly } = useContext(CaseContext);
	const isDisabled = isCaseReadOnly || isPublished;
	const [brandSearch, setBrandSearch] = React.useState<string>('');
	const [searchFilter] = useDebounce(brandSearch, 500);
	const {
		register,
		setValue,
		formState: { errors },
	} = useFormContext();

	const requiredFieldRule = {
		required: {
			value: true,
			message: 'Please fill out the required field.',
		},
	};

	const isSameDate = (dateString: string): boolean => {
		const inputDate = new Date(dateString);
		const today = new Date();
		return (
			inputDate.getUTCFullYear() === today.getUTCFullYear() &&
			inputDate.getUTCMonth() === today.getUTCMonth() &&
			inputDate.getUTCDate() === today.getUTCDate()
		);
	};

	const fetchData = async ({ pageParam = 1 }): Promise<ListBrands> => {
		const currentFilter =
			refCreation.current && searchFilter.length >= 3
				? searchFilter.slice(0, 3)
				: searchFilter;
		const response = await brandsService.getPaginatedBrands(
			currentFilter,
			pageParam,
			20
		);
		refCreation.current = false;
		return response.data;
	};

	const {
		data: brandsData,
		refetch: refetchBrands,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading: isBrandsQueryLoading,
	} = useInfiniteQuery({
		queryKey: [GET_BRANDS, searchFilter],
		queryFn: fetchData,
		getNextPageParam: (lastPage) => {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		initialPageParam: 1,
	});

	const observer = React.useRef<IntersectionObserver | null>(null);
	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | null) => {
			if (isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});
			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage, fetchNextPage]
	);

	const listCompanies = React.useMemo(() => {
		const listBrands: Option[] = [
			{
				label: 'Select',
				name: '',
				placeholder: true,
			},
		];

		if (brandsData?.pages) {
			brandsData.pages.forEach((page) => {
				page.items.forEach((brand) => {
					listBrands.push({
						label: brand.name,
						name: brand.id.toString(),
						flag: isSameDate(brand.updated_at),
					});
				});
			});
		}

		return listBrands;
	}, [brandsData]);

	const handleSetValue = (name: string, value: any) => {
		setValue(name, value, {
			shouldDirty: true,
		});
	};

	const handleDeleteBrand = async (id: string) => {
		try {
			await brandsService.delete(id);
			refetchBrands();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const handleBrand = async (
		value: string,
		callback: any,
		id: string | number | undefined
	) => {
		try {
			const response = id
				? await brandsService.update(value, id as string)
				: await brandsService.create(value);

			refCreation.current = true;
			await refetchBrands();
			toastSuccess(response.data.message);
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			callback();
		}
	};

	const listStatesWithoutSelectAll = listStates?.filter(
		(option) => option.name !== 'all'
	) || [
		{
			name: '',
			label: 'Select',
			placeholder: true,
		},
	];

	return (
		<div className="!mt-8 py-8 border-y border-gray-200">
			<Typography type="semibold-20" className="!text-[20px] mb-5">
				Company Information
			</Typography>

			<div>
				<div className="flex flex-col gap-8 bg-grey-2 p-6 rounded-2xl">
					{fields.map((item: any, index) => {
						const machBrand = caseDetails?.brands?.find(
							(brand) => brand.id === item.brandId
						);
						const machCorporation = caseDetails?.brands?.find(
							(brand) =>
								brand.defendant_incorporation.id?.toString() ===
								item.defendantIncorporation
						);
						const machPlaceOfBusiness = caseDetails?.brands?.find(
							(brand) => brand.entity_type === item.entityType
						);
						const machEntityType = caseDetails?.brands?.find(
							(brand) => brand.entity_type?.toString() === item.entityType
						);

						const defaultBrand: Option =
							machBrand?.name && machBrand.id
								? {
										label: machBrand.name,
										name: machBrand.id?.toString() || '',
								  }
								: { label: 'Select', name: '', placeholder: true };
						const defaultDefendantIncorporation: Option =
							machCorporation?.defendant_incorporation.name &&
							machCorporation.defendant_incorporation.id !== null
								? {
										label: machCorporation.defendant_incorporation.name,
										name: machCorporation.defendant_incorporation.id?.toString(),
								  }
								: { label: 'Select', name: '', placeholder: true };
						const defaultPlaceOfBusiness: Option =
							machPlaceOfBusiness?.principal_place_of_business.name !== null &&
							machPlaceOfBusiness?.principal_place_of_business.id
								? {
										label:
											machPlaceOfBusiness.principal_place_of_business.id?.toString(),
										name: machPlaceOfBusiness.principal_place_of_business.name?.toString(),
								  }
								: { label: 'Select', name: '', placeholder: true };

						const defaultEntityType: Option = machEntityType?.entity_type
							? {
									label: machEntityType.entity_type,
									name: machEntityType.entity_type,
							  }
							: { label: 'Select', name: '', placeholder: true };
						return (
							<div
								key={item.id}
								className={clsx('flex flex-col gap-4', {
									'pt-6 border-t border-[#E4E7EC]': index > 0,
								})}
							>
								<div className="flex justify-between gap-x-2 w-full">
									<Typography type="semibold-14" className="text-black">
										Company {index + 1}
									</Typography>
									{fields.length > 1 && (
										<button type="button" onClick={() => remove(index)}>
											<Icon
												src={Icons.trash}
												className="text-[#707070]"
												width={24}
											/>
										</button>
									)}
								</div>
								<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
									<div className="flex-1 w-full">
										<InputList
											disabled={isDisabled}
											myDefaultValue={defaultBrand.name}
											loading={isBrandsQueryLoading}
											containerRef={lastItemCallback}
											isSearch
											showActionButtons
											name={`companies[${index}].brandId`}
											title="Defendant Name of Company"
											register={register}
											options={listCompanies}
											setValueInput={handleSetValue}
											error={
												Array.isArray(errors.companies)
													? errors.companies[index]?.brandId
													: null
											}
											setInputCreate={handleBrand}
											setInputDelete={handleDeleteBrand}
											handleSearch={(value) => setBrandSearch(value)}
											rules={applyRule ? requiredFieldRule : undefined}
											classNameInput="!mt-2"
											classNameIcon="!top-[28%] md:!top-[38%]"
											classNameTitle="!text-[16px]"
										/>
									</div>
									<div className="flex-1 w-full">
										<InputListV3
											disabled={isDisabled}
											name={`companies[${index}].entityType`}
											defaultOption={defaultEntityType}
											title="Entity Type"
											register={register}
											options={listEntityTypes}
											setValueInput={handleSetValue}
											rules={applyRule ? requiredFieldRule : undefined}
											error={
												Array.isArray(errors.companies)
													? errors.companies[index]?.entityType
													: null
											}
											classNameInput="!mt-2"
											classNameIcon="!top-[38%]"
											classNameTitle="!text-[16px]"
											classNameLabel="!text-[16px]"
										/>
									</div>
								</div>

								<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
									<div className="flex-1 w-full">
										<InputListV3
											isSearch
											disabled={isDisabled}
											name={`companies[${index}].defendantIncorporation`}
											defaultOption={defaultDefendantIncorporation}
											title="Defendant Incorporation"
											register={register}
											options={listStatesWithoutSelectAll}
											setValueInput={handleSetValue}
											rules={applyRule ? requiredFieldRule : undefined}
											error={
												Array.isArray(errors.companies)
													? errors.companies[index]?.defendantIncorporation
													: null
											}
											classNameInput="!mt-2"
											classNameIcon="!top-[38%]"
											classNameTitle="!text-[16px]"
											classNameLabel="!text-[16px]"
										/>
									</div>
									<div className="flex-1 w-full">
										<InputListV3
											isSearch
											disabled={isDisabled}
											name={`companies[${index}].princpialPlaceOfBusiness`}
											defaultOption={defaultPlaceOfBusiness}
											title="Principal Place of Business"
											register={register}
											options={listStatesWithoutSelectAll}
											setValueInput={handleSetValue}
											rules={applyRule ? requiredFieldRule : undefined}
											error={
												Array.isArray(errors.companies)
													? errors.companies[index]?.princpialPlaceOfBusiness
													: null
											}
											classNameInput="!mt-2"
											classNameIcon="!top-[38%]"
											classNameTitle="!text-[16px]"
											classNameLabel="!text-[16px]"
										/>
									</div>
								</div>
							</div>
						);
					})}
				</div>

				<div className="flex gap-6 mt-6">
					<Button
						disabled={isDisabled}
						iconLeft
						icon={Icons.plus2}
						classNameButton="!text-[#B18EE0] !h-4 !w-4"
						label="Add a Company"
						decoration="line-primary"
						className="!py-[5.5px] !px-6 h-[30px]"
						size="fit"
						onClick={() =>
							append({
								entityType: '',
								brandId: '',
								defendantIncorporation: '',
								princpialPlaceOfBusiness: '',
							})
						}
					/>
				</div>
			</div>
		</div>
	);
};

export default CaseCompanyInformation;
